import { isTimeZone } from "../../shared/time-zone";

/**
 * Time zones: every zone the browser knows, named the way a person says them
 * and found by a city or a country, plus the workspace's own zone that new lives
 * start in and the app shows times in.
 */

/** The zones most hosts are in, named the way a person says them. Shown first. */
const COMMON_ZONES: ReadonlyArray<{ zone: string; label: string }> = [
  { zone: "America/New_York", label: "Eastern Time" },
  { zone: "America/Chicago", label: "Central Time" },
  { zone: "America/Denver", label: "Mountain Time" },
  { zone: "America/Phoenix", label: "Arizona" },
  { zone: "America/Los_Angeles", label: "Pacific Time" },
  { zone: "America/Anchorage", label: "Alaska" },
  { zone: "Pacific/Honolulu", label: "Hawaii" },
  { zone: "America/Sao_Paulo", label: "Brazil" },
  { zone: "Europe/London", label: "London" },
  { zone: "Europe/Paris", label: "Central Europe" },
  { zone: "Africa/Johannesburg", label: "South Africa" },
  { zone: "Asia/Dubai", label: "Dubai" },
  { zone: "Asia/Kolkata", label: "India" },
  { zone: "Asia/Singapore", label: "Singapore" },
  { zone: "Asia/Tokyo", label: "Japan" },
  { zone: "Australia/Sydney", label: "Sydney" },
  { zone: "Pacific/Auckland", label: "New Zealand" },
];

/**
 * Cities and countries a person types that the zone's own name does not say:
 * nobody looks for India under "Kolkata", or for Texas under "Chicago".
 */
const ALSO_KNOWN_AS: Record<string, string> = {
  "America/New_York": "New York Boston Washington Miami Atlanta Philadelphia Charleston Orlando Charlotte Florida Georgia Carolina Ohio Michigan Eastern USA United States",
  "America/Chicago": "Chicago Dallas Houston Austin San Antonio Nashville Minneapolis Kansas City St Louis New Orleans Texas Illinois Tennessee Central USA",
  "America/Denver": "Denver Salt Lake City Albuquerque Boise Colorado Utah Mountain",
  "America/Phoenix": "Phoenix Tucson Arizona",
  "America/Los_Angeles": "Los Angeles San Francisco San Diego San Jose Seattle Portland Las Vegas Sacramento California Washington state Oregon Nevada Pacific",
  "America/Toronto": "Toronto Ottawa Montreal Quebec Ontario Canada",
  "America/Vancouver": "Vancouver British Columbia Canada",
  "America/Mexico_City": "Mexico City Guadalajara Monterrey Mexico",
  "America/Bogota": "Bogota Medellin Colombia",
  "America/Sao_Paulo": "Sao Paulo Rio de Janeiro Brazil",
  "America/Argentina/Buenos_Aires": "Buenos Aires Argentina",
  "Europe/London": "London Manchester Edinburgh UK United Kingdom England Britain Scotland",
  "Europe/Dublin": "Dublin Ireland",
  "Europe/Paris": "Paris France",
  "Europe/Berlin": "Berlin Munich Frankfurt Hamburg Germany",
  "Europe/Madrid": "Madrid Barcelona Spain",
  "Europe/Rome": "Rome Milan Italy",
  "Europe/Amsterdam": "Amsterdam Netherlands Holland",
  "Europe/Zurich": "Zurich Geneva Switzerland",
  "Europe/Stockholm": "Stockholm Sweden",
  "Europe/Warsaw": "Warsaw Poland",
  "Europe/Istanbul": "Istanbul Ankara Turkey",
  "Europe/Moscow": "Moscow Russia",
  "Asia/Dubai": "Dubai Abu Dhabi Sharjah UAE United Arab Emirates",
  "Asia/Riyadh": "Riyadh Jeddah Saudi Arabia",
  "Asia/Qatar": "Doha Qatar",
  "Asia/Karachi": "Karachi Lahore Islamabad Pakistan",
  "Asia/Kolkata": "India Chennai Mumbai Delhi New Delhi Bengaluru Bangalore Hyderabad Pune Ahmedabad Kolkata Kochi",
  "Asia/Dhaka": "Dhaka Bangladesh",
  "Asia/Bangkok": "Bangkok Thailand",
  "Asia/Jakarta": "Jakarta Indonesia",
  "Asia/Singapore": "Singapore",
  "Asia/Kuala_Lumpur": "Kuala Lumpur Malaysia",
  "Asia/Manila": "Manila Philippines",
  "Asia/Hong_Kong": "Hong Kong",
  "Asia/Shanghai": "Beijing Shanghai Shenzhen Guangzhou China",
  "Asia/Tokyo": "Tokyo Osaka Japan",
  "Asia/Seoul": "Seoul South Korea",
  "Africa/Lagos": "Lagos Abuja Nigeria",
  "Africa/Cairo": "Cairo Egypt",
  "Africa/Nairobi": "Nairobi Kenya",
  "Africa/Johannesburg": "Johannesburg Cape Town Durban South Africa",
  "Australia/Sydney": "Sydney Canberra New South Wales Australia",
  "Australia/Melbourne": "Melbourne Victoria Australia",
  "Australia/Brisbane": "Brisbane Queensland Australia",
  "Australia/Perth": "Perth Western Australia",
  "Pacific/Auckland": "Auckland Wellington Christchurch New Zealand",
};

/** The name Intl settles on for a zone, so an alias and its zone compare equal. */
export function canonical(zone: string): string {
  try {
    return new Intl.DateTimeFormat("en-US", { timeZone: zone }).resolvedOptions().timeZone;
  } catch {
    return zone;
  }
}

/**
 * The browser's own zone. A browser can report an older name for a common zone
 * (Chrome says Asia/Calcutta for India), so the common entry is used when it is
 * the same zone.
 */
export function browserZone(): string {
  let own = "UTC";
  try {
    own = Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
  } catch {
    return own;
  }
  const same = COMMON_ZONES.find((z) => canonical(z.zone) === canonical(own));
  return same ? same.zone : own;
}

/** A zone in plain words: its common name, else its city ("America/Sao_Paulo" is "Sao Paulo"). */
function zoneLabel(zone: string): string {
  const known = COMMON_ZONES.find((z) => z.zone === zone);
  if (known) return known.label;
  return (zone.split("/").pop() ?? zone).replace(/_/g, " ");
}

/** One part of a zone's name as Intl says it today: "GMT+5:30", "India Standard Time". */
function zoneName(zone: string, style: "shortOffset" | "longGeneric", at: number): string {
  try {
    const parts = new Intl.DateTimeFormat("en-US", { timeZone: zone, timeZoneName: style }).formatToParts(at);
    return parts.find((p) => p.type === "timeZoneName")?.value ?? "";
  } catch {
    return "";
  }
}

/** "GMT+5:30" as minutes east of Greenwich, for putting zones in order. */
function offsetMinutes(offset: string): number {
  const m = /GMT([+-])(\d{1,2})(?::(\d{2}))?/.exec(offset);
  if (!m) return 0;
  return (m[1] === "-" ? -1 : 1) * (Number(m[2]) * 60 + Number(m[3] ?? 0));
}

export interface ZoneOption {
  zone: string;
  /** "India", "Eastern Time", or the city. */
  label: string;
  /** "India Standard Time · GMT+5:30". */
  line: string;
  offset: number;
  /** Everything a search may match, lower case. */
  words: string;
}

function optionFor(zone: string, at: number): ZoneOption {
  const offset = zoneName(zone, "shortOffset", at);
  const long = zoneName(zone, "longGeneric", at);
  const label = zoneLabel(zone);
  const city = (zone.split("/").pop() ?? zone).replace(/_/g, " ");
  // "Eastern Time · GMT-4" under a row named Eastern Time says the name twice: the offset is enough.
  const line = [long && long !== offset && long !== label ? long : "", offset].filter(Boolean).join(" · ");
  const words = [label, city, long, offset, zone.replace(/[_/]/g, " "), ALSO_KNOWN_AS[zone] ?? ""].join(" ").toLowerCase();
  return { zone, label, line, offset: offsetMinutes(offset), words };
}

let cached: { at: number; list: ZoneOption[] } | null = null;

/** Every zone the browser knows, west to east, worked out once an hour (offsets move with summer time). */
export function allZones(now: number = Date.now()): ZoneOption[] {
  if (cached && now - cached.at < 3_600_000) return cached.list;
  let names: string[];
  try {
    names = (Intl as unknown as { supportedValuesOf?: (k: string) => string[] }).supportedValuesOf?.("timeZone") ?? [];
  } catch {
    names = [];
  }
  const zones = new Set([...COMMON_ZONES.map((z) => z.zone), ...names]);
  const list = [...zones].map((z) => optionFor(z, now)).sort((a, b) => a.offset - b.offset || a.label.localeCompare(b.label));
  cached = { at: now, list };
  return list;
}

/** The common zones, in their own order, then any extra the caller wants on top (the chosen one, this device's). */
export function commonZones(extra: Array<string | null | undefined> = [], now: number = Date.now()): ZoneOption[] {
  const all = allZones(now);
  const byZone = new Map(all.map((o) => [o.zone, o]));
  const want = [...extra.filter((z): z is string => !!z), ...COMMON_ZONES.map((z) => z.zone)];
  const seen = new Set<string>();
  const out: ZoneOption[] = [];
  for (const z of want) {
    const key = canonical(z);
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(byZone.get(z) ?? optionFor(z, now));
  }
  return out;
}

/** The zones whose name, city, country or offset holds every word typed. */
export function findZones(query: string, now: number = Date.now()): ZoneOption[] {
  const words = query.toLowerCase().split(/\s+/).filter(Boolean);
  if (!words.length) return allZones(now);
  return allZones(now).filter((o) => words.every((w) => o.words.includes(w)));
}

/** "India · GMT+5:30": what a picked zone reads as on a button or a row. */
export function zoneSummary(zone: string, now: number = Date.now()): string {
  const offset = zoneName(zone, "shortOffset", now);
  return offset ? `${zoneLabel(zone)} · ${offset}` : zoneLabel(zone);
}

// ── the workspace's zone ───────────────────────────────────────────────

let workspaceZone: string | null = null;

/** Set from the signed-in workspace: null leaves each device on its own zone. */
export function setWorkspaceZone(zone: string | null | undefined): void {
  workspaceZone = zone && isTimeZone(zone) ? zone : null;
}

/** The zone the app shows times in: the workspace's, or undefined for this device's own. */
export function appZone(): string | undefined {
  return workspaceZone ?? undefined;
}

/** The zone a new live starts in: the workspace's, else this device's. */
export function homeZone(): string {
  return workspaceZone ?? browserZone();
}

/** A moment as words in the app's zone, the way `toLocaleDateString` would say it. */
export function formatAt(at: number | Date, opts: Intl.DateTimeFormatOptions): string {
  return new Intl.DateTimeFormat("en-US", { ...opts, timeZone: appZone() }).format(at);
}

/** The calendar day a moment falls on in the app's zone, as a number two moments can be compared by. */
export function dayNumber(at: number | Date): number {
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone: appZone(),
    year: "numeric",
    month: "numeric",
    day: "numeric",
  }).formatToParts(at);
  const get = (t: string) => Number(parts.find((p) => p.type === t)?.value ?? 0);
  return Date.UTC(get("year"), get("month") - 1, get("day")) / 86_400_000;
}
